import Graph from 'graphology';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import { useCallback } from 'react';

import {
  calculateLayoutSettleTime,
  computeLayoutParams,
  FA2_ALGORITHM_SETTINGS,
} from '@/components/nodeGraph/config/graphConfig';

/**
 * useGraphLayout - Graph Layout Management Hook
 *
 * This hook manages the creation and lifecycle of force-directed layouts
 * for the graph visualization, providing optimized layout parameters
 * and automatic cleanup.
 *
 * Features:
 * - Dynamic layout parameter calculation based on graph size
 * - Optimized FA2Layout configuration
 * - Automatic layout timing and cleanup
 * - Performance optimization for large graphs
 *
 * @param _isInitialLoad - Whether this is the initial load (unused for now)
 */
export const useGraphLayout = (_isInitialLoad: boolean) => {
  const createLayout = useCallback((graph: Graph) => {
    const params = computeLayoutParams(graph.order);

    const layout = new FA2Layout(graph, {
      settings: {
        ...FA2_ALGORITHM_SETTINGS,
        gravity: params.gravity,
        scalingRatio: params.scalingRatio,
      },
    });

    return layout;
  }, []);

  const startLayout = useCallback((layout: FA2Layout, graph: Graph) => {
    // Start the layout and update running state
    layout.start();

    // Use centralized settle time calculation
    const settleTime = calculateLayoutSettleTime(graph.order);

    // Stop the layout after the dynamic delay
    setTimeout(() => {
      if (layout.isRunning()) {
        layout.stop();
      }
    }, settleTime);

    // Store the layout instance in the graph
    graph.setAttribute('layout', layout);
  }, []);

  const createAndStartLayout = useCallback(
    (_graph: Graph) => {
      // Disable layout completely for static positioning
      // const layout = createLayout(graph);
      // startLayout(layout, graph);
      // return layout;
      return null;
    },
    [createLayout, startLayout]
  );

  const resetGraphLayout = useCallback((graph: Graph) => {
    if (graph) {
      const layout = graph.getAttribute('layout') as FA2Layout;
      if (layout) {
        layout.stop();
      }
    }
  }, []);

  return {
    createLayout,
    startLayout,
    createAndStartLayout,
    resetGraphLayout,
  };
};
